class ResponsiveGridCarousel {
  constructor(element, options = {}) {
    if (!element) {
      console.warn('ResponsiveGridCarousel: No element provided');
      return;
    }    
    this.container = element;    
    this.track = element.querySelector('.grid_carousel_track') || element.querySelector('.modal_grid_carousel_track');
    if (!this.track) {
      console.warn('ResponsiveGridCarousel: No carousel track found inside', element);
      return;
    }    
    this.items = Array.from(this.track.children);
    if (this.items.length === 0) {
      console.warn('ResponsiveGridCarousel: No items found in carousel track');
      return;
    }    
    this.options = {
      friction: 0.88,
      ease: 0.2,
      velocityMultiplier: 4.0,
      snapTolerance: 2.5,
      snapEase: 0.3,
      nav: true,
      dots: true,
      mouseWheel: true,
      stopThreshold: 0.001,
      responsive: {
        0:    { items: 1 },
        600:  { items: 2 },
        900:  { items: 3 },
        1200: { items: 4 }
      },
      ...options
    };
    this.currentX = 0;
    this.targetX = 0;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0; 
    this.dragDistance = 0; 
    this.currentVelocity = 0; 
    this.isRunning = false; 
    this.itemWidth = 0;
    this.gap = 0;
    this.viewWidth = 0;
    this.maxScroll = 0;
    this.itemsToShow = 1;
    this.prevBtn = null;
    this.nextBtn = null;
    this.dots = null;    
    this.init(); 
  }
  init() {
    this.addEvents();
    this.calculateLayout();     
    if(this.options.nav) this.buildNav();
    if(this.options.dots) this.buildDots();    
    this.startAnimation(); 
  }
  startAnimation() {
    if (!this.isRunning) {
      this.isRunning = true;
      this.animate();
    }
  }
  stopAnimation() {
    this.isRunning = false;
  }
  calculateLayout() {
    const width = window.innerWidth;
    const breakpoints = Object.keys(this.options.responsive).map(Number).sort((a,b)=>a-b);
    let activeConfig = this.options.responsive[0];    
    breakpoints.forEach(bp => {
      if (width >= bp) {
        activeConfig = this.options.responsive[bp];
      }
    });
    
    // ⚡ Cap itemsToShow to actual number of items available
    this.itemsToShow = Math.min(activeConfig.items, this.items.length);
    
    const containerWidth = this.container.getBoundingClientRect().width;    
    const computedStyle = window.getComputedStyle(this.track);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const totalStagePadding = paddingLeft + paddingRight;
    // Adjust visible width for calculations
    const paddedContainerWidth = containerWidth - totalStagePadding;    
    // Get gap and calculate item width
    const gapStyle = computedStyle.columnGap;
    this.gap = parseFloat(gapStyle) || 0;
    const totalGapSpace = this.gap * (this.itemsToShow - 1);
    const calculatedItemWidth = (paddedContainerWidth - totalGapSpace) / this.itemsToShow;
    // Apply layout changes
    this.track.style.gridAutoColumns = `${calculatedItemWidth}px`;
    this.itemWidth = calculatedItemWidth;
    this.viewWidth = containerWidth; 
    // Calculate Max Scroll 
    const totalContentWidth = (this.itemWidth * this.items.length) + (this.gap * (this.items.length - 1));    
    this.maxScroll = -(totalContentWidth - paddedContainerWidth);    
    if(this.maxScroll > 0) this.maxScroll = 0;    
    // Clamping position to 0 
    const initialPos = 0;     
    this.targetX = Math.max(this.maxScroll, Math.min(initialPos, this.targetX));
    this.currentX = this.targetX;    
    this.track.style.transform = `translate3d(${this.currentX}px, 0, 0)`;
    if(this.options.dots) this.buildDots();
  }
  animate() {
    if (!this.isRunning) return; 
    const difference = this.targetX - this.currentX;
    if (!this.isDragging) {
      // Apply velocity-based movement
      if (Math.abs(this.currentVelocity) > this.options.snapTolerance) { 
        this.targetX += this.currentVelocity;
        this.currentVelocity *= this.options.friction;
      } else {
        // Only snap when velocity has died down
        this.currentVelocity = 0;        
        const slotSize = this.itemWidth + this.gap;
        
        // ⚡ KEY FIX: Find the NEAREST snap point based on how far we've traveled
        // Don't always round - check which direction we moved
        const currentSlot = this.targetX / slotSize;
        let snapTarget;
        
        // If we're close to a slot already, snap to nearest
        const fractionalPart = Math.abs(currentSlot - Math.round(currentSlot));
        if (fractionalPart < 0.15) {
          // Very close to a snap point - snap to nearest
          snapTarget = Math.round(this.targetX / slotSize) * slotSize;
        } else {
          // We've moved significantly - snap in the direction of movement
          snapTarget = Math.floor(currentSlot) * slotSize;
        }
        
        if(Math.abs(this.targetX - snapTarget) > this.options.stopThreshold) {
          this.targetX += (snapTarget - this.targetX) * this.options.snapEase;
        } else {
          this.targetX = snapTarget;
        }
      }
    }
    const overscroll = 0; 
    const startPos = 0; 
    this.targetX = Math.max(this.maxScroll - overscroll, Math.min(startPos + overscroll, this.targetX));    
    this.currentX += difference * this.options.ease;
    if (!this.isDragging && Math.abs(difference) < this.options.stopThreshold) {
      this.currentX = this.targetX; 
      this.track.style.transform = `translate3d(${this.currentX}px, 0, 0)`;
      this.stopAnimation();        
      if(this.options.dots) this.updateDotsActive();
      if(this.options.nav) this.updateNavState();
      return; 
    }
    this.track.style.transform = `translate3d(${this.currentX}px, 0, 0)`;
    if(this.options.dots) this.updateDotsActive();
    if(this.options.nav) this.updateNavState();
    requestAnimationFrame(this.animate.bind(this));
  }  
  addEvents() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.calculateLayout();
        this.startAnimation(); 
      }, 100);
    });
    this.track.addEventListener('dragstart', e => {
      e.preventDefault();
      return false;
    });
    // Pointerdown stays on track (already scoped to this instance)
    this.track.addEventListener('pointerdown', e => {
      if (e.button === 2) {
        return;
      }
      // ⚡ Don't allow dragging if content fits in viewport
      if (this.maxScroll >= 0) {
        return;
      }
      this.isDragging = true;
      this.startX = e.clientX;
      this.startY = e.clientY; 
      this.dragDistance = 0; 
      this.currentVelocity = 0;
      this.track.setPointerCapture(e.pointerId);
      this.startAnimation();
    });
    // Move pointermove to track instead of window
    this.track.addEventListener('pointermove', e => {
      if (!this.isDragging) return;
      const x = e.clientX;
      const y = e.clientY;       
      const diffX = x - this.startX;
      const diffY = y - this.startY;       
      this.targetX += diffX;      
      this.dragDistance += Math.sqrt(diffX * diffX + diffY * diffY);       
      this.currentVelocity = diffX;
      this.startX = x;
      this.startY = y; 
    });
    // Move pointerup to track instead of window
    this.track.addEventListener('pointerup', e => {
      if (e.button === 2) {
        return;
      }
      // Release pointer capture
      if (e.pointerId !== undefined && this.track.hasPointerCapture(e.pointerId)) {
        this.track.releasePointerCapture(e.pointerId);
      }      
      console.log(`PointerUp Fired. Total Drag Distance: ${this.dragDistance.toFixed(2)}px`);      
      // If drag distance is very small (< 10px), treat it as a click/tap
      if (this.dragDistance < 10) { 
        console.log(`✅ Drag distance is below 10px. Treating as a click.`);          
        // ⚡ KEY FIX: Use elementFromPoint to get the ACTUAL element under the cursor
        // because pointer capture redirects e.target to the track element
        let target = document.elementFromPoint(e.clientX, e.clientY);
        console.log(`🔍 Actual element at cursor:`, target);          
        // Check for clickable elements
        const cardContainer = target.closest('.product_card_container');
        const linkTarget = target.closest('a');
        if (cardContainer) {
          console.log(`🎯 Found Card Container (onclick):`, cardContainer);
        } else if (linkTarget) {
          console.log(`🎯 Found Anchor Link (href):`, linkTarget);
        } else {
          console.log(`❌ No clickable card or link found. Target element:`, target);
        }
        // Handle color variant links FIRST (they are more specific)
        if (linkTarget && linkTarget.href) {
          console.log(`🚀 Navigating to anchor link: ${linkTarget.href}`);
          e.preventDefault(); // ⚡ KEY FIX: Only prevent default when we handle it
          window.location.href = linkTarget.href;
          this.isDragging = false;
          this.currentVelocity = 0; // Reset velocity for clicks
          return; 
        }          
        // Handle card container onclick (less specific, check after links)
        if (cardContainer && !linkTarget) {
          const onclickAttr = cardContainer.getAttribute('onclick');
          if (onclickAttr && onclickAttr.startsWith('window.location')) {
            console.log(`🚀 Executing manual navigation: ${onclickAttr}`);
            e.preventDefault(); // ⚡ KEY FIX: Only prevent default when we handle it
            eval(onclickAttr); 
            this.isDragging = false;
            this.currentVelocity = 0; // Reset velocity for clicks
            return; 
          }
        }
        // ⚡ If it's a click but not on a link/card, reset velocity
        this.currentVelocity = 0;
      } else {
        console.log(`🚫 Drag distance is 10px or more. Treating as drag, ignoring click.`);
        // ⚡ Apply velocity multiplier for actual drags/flicks
        this.currentVelocity *= this.options.velocityMultiplier;
      }
      this.isDragging = false;
    });    
    // Wheel event - only add if mouseWheel option is enabled
    if (this.options.mouseWheel) {
      this.container.addEventListener('wheel', e => {
        // ⚡ Don't allow wheel scrolling if content fits in viewport
        if (this.maxScroll >= 0) {
          return;
        }
        this.currentVelocity = 0; 
        const direction = e.deltaY < 0 ? 1 : -1; 
        const slot = this.itemWidth + this.gap;
        const currentIndex = Math.round(this.targetX / slot);
        const targetIndex = currentIndex + direction;          
        let newTargetX = targetIndex * slot;          
        newTargetX = Math.max(this.maxScroll, Math.min(0, newTargetX));
        this.targetX = newTargetX;
        e.preventDefault(); 
        this.startAnimation();
      }, {passive: false});
    }
  }
  moveBy(dir) {
    const slot = this.itemWidth + this.gap;    
    const nextTarget = Math.round(this.targetX / slot) * slot + (dir * slot);    
    let newTargetX = nextTarget;    
    const startPos = 0;
    newTargetX = Math.max(this.maxScroll, Math.min(startPos, newTargetX));    
    this.targetX = newTargetX;
    this.startAnimation();
  }  
  buildNav() {
    if (!this.options.nav) return;    
    const existingPrev = this.container.querySelector('.gc-prev');
    if(existingPrev) existingPrev.remove();
    const existingNext = this.container.querySelector('.gc-next');
    if(existingNext) existingNext.remove();
    
    // ⚡ Don't show nav if content fits in viewport
    if (this.maxScroll >= 0) {
      this.prevBtn = null;
      this.nextBtn = null;
      return;
    }
    
    this.prevBtn = document.createElement('button');
    this.prevBtn.className = 'gc-nav-btn gc-prev';
    this.prevBtn.innerHTML = '&larr;';
    this.prevBtn.onclick = () => this.moveBy(1);
    this.nextBtn = document.createElement('button');
    this.nextBtn.className = 'gc-nav-btn gc-next';
    this.nextBtn.innerHTML = '&rarr;';
    this.nextBtn.onclick = () => this.moveBy(-1);
    this.container.appendChild(this.prevBtn);
    this.container.appendChild(this.nextBtn);
  }
  updateNavState() {
    if (!this.options.nav || !this.prevBtn || !this.nextBtn) return;
    const tolerance = 10;
    const startPos = 0;
    this.prevBtn.disabled = this.currentX >= startPos - tolerance;
    this.nextBtn.disabled = this.currentX <= this.maxScroll + tolerance;
  }  
  buildDots() {
    if (!this.options.dots) return;
    const existingInside = this.container.querySelector('.gc-dots');
    if(existingInside) existingInside.remove();
    let sibling = this.container.nextElementSibling;
    while(sibling && sibling.classList.contains('gc-dots')) {
      const toRemove = sibling;
      sibling = sibling.nextElementSibling;
      toRemove.remove();
    }
    
    // ⚡ Don't show dots if content fits in viewport (maxScroll is 0 or positive)
    if (this.maxScroll >= 0) {
      this.dots = null;
      return;
    }
    
    const dotsCont = document.createElement('div');
    dotsCont.className = 'gc-dots';    
    const totalSteps = Math.ceil(this.items.length - this.itemsToShow) + 1;  
    // Don't show dots if there's only 1 step needed
    if (totalSteps <= 1) {
      return;
    }    
    const numDots = Math.max(1, totalSteps);
    // Don't show dots if there's only 1 dot needed
    if (numDots <= 1) {
      return;
    }     
    const slot = this.itemWidth + this.gap;
    for (let i = 0; i < numDots; i++) {
      const dot = document.createElement('div');
      dot.className = 'gc-dot';        
      let targetXPosition = -(slot * i);        
      if (i === numDots - 1) {
        targetXPosition = this.maxScroll;
      }
      dot.onclick = () => {
        this.targetX = targetXPosition;
        this.startAnimation();
      }
      dotsCont.appendChild(dot);
    }
    this.container.parentNode.insertBefore(dotsCont, this.container.nextSibling);
    this.dots = Array.from(dotsCont.children);
  }
  updateDotsActive() {
    if (!this.options.dots || !this.dots || this.dots.length === 0) return;
    const slot = this.itemWidth + this.gap;
    let closestIndex = 0;
    for (let i = 0; i < this.dots.length; i++) {
      let dotTargetX = -(slot * i);        
      if (i === this.dots.length - 1) {
        dotTargetX = this.maxScroll;
      }
      const distance = Math.abs(this.currentX - dotTargetX);
      if (i === 0 || distance < Math.abs(this.currentX - (-(slot * closestIndex)))) {
        closestIndex = i;
      }
    }    
    if (this.currentX <= this.maxScroll + 1) {
      closestIndex = this.dots.length - 1;
    }
    this.dots.forEach((dot, i) => {
      if(i === closestIndex) dot.classList.add('active');
      else dot.classList.remove('active');
    });
  }
}